import { useEffect } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { useMutation } from '@apollo/client';

import { useErrorToast, useInfoToast } from '../components/Toast';
import PLAID_VERIFICATION_STATUS from '../constants/plaid_verifications/plaid_verification_status';
import basicUserProfileGQL from '../graphql/basicUserProfile.graphql';
import cancelPlaidVerificationsMutationGQL from '../graphql/mutations/plaid_verifications/cancelPlaidVerificationsMutation.graphql';
import finishPlaidVerificationMutationGQL from '../graphql/mutations/plaid_verifications/finishPlaidVerificationMutation.graphql';

import usePlaidLinkToken from './usePlaidLinkToken';

const useVerifyPlaidLink = (paymentMethodId) => {
  const [finishPlaidVerificationMutation, { loading: verifying }] = useMutation(
    finishPlaidVerificationMutationGQL,
  );

  const [cancelPlaidVerificationsMutation, { loading: canceling }] =
    useMutation(cancelPlaidVerificationsMutationGQL);

  const showErrorMessage = useErrorToast();
  const showInfoMessage = useInfoToast();

  const { linkToken, retrySettingLinkToken } = usePlaidLinkToken({
    paymentMethodId,
  });

  const handleBankAddViaPlaid = async (plaidPublicToken, accounts, isRetry) => {
    let plaidAccountId;
    if (!isRetry) {
      plaidAccountId = accounts?.account_id;
      const verificationStatus = accounts?.account?.verification_status;

      if (
        !isRetry &&
        verificationStatus !== PLAID_VERIFICATION_STATUS.MANUALLY_VERIFIED
      ) {
        showErrorMessage('Your bank account is not verified yet');
        return;
      }
    }

    try {
      const res = await finishPlaidVerificationMutation({
        variables: {
          ...(!isRetry && {
            plaid_public_token: plaidPublicToken,
            plaid_account_id: plaidAccountId,
          }),
          paymentMethodId: paymentMethodId,
          is_retry: isRetry,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: basicUserProfileGQL,
          },
        ],
      });

      if (res.data.finishPlaidVerificationMutation.ok) {
        showInfoMessage('You have successfully verified your bank account!');
      }
    } catch {
      showErrorMessage('An error has occurred');
    }
  };

  const handleFailedVerification = async (errorCode) => {
    try {
      const res = await cancelPlaidVerificationsMutation({
        variables: {
          errorCode,
        },
        refetchQueries: [
          {
            query: basicUserProfileGQL,
          },
        ],
      });

      if (res.data.cancelPlaidVerificationsMutation.ok) {
        showErrorMessage(
          'Verification failed, attempts exceeded. Please re-add a payment method',
        );
      }
    } catch {
      showErrorMessage('An error has occurred');
    }
  };

  const retryMicrodepositVerification = async () =>
    handleBankAddViaPlaid(null, null, true);

  const { open, ready, error } = usePlaidLink({
    token: linkToken,
    onSuccess: handleBankAddViaPlaid,
    onExit: (error, metadata) => {
      if (error) {
        const { error_code: errorCode } = error;
        if (errorCode === 'MICRODEPOSITS_ALREADY_VERIFIED') {
          retryMicrodepositVerification();
        } else if (errorCode === 'INVALID_LINK_TOKEN') {
          retrySettingLinkToken(true);
        } else {
          handleFailedVerification(errorCode);
        }
      }
      // Means that this wasn't a user-invoked action but no error is present.
      else if (!metadata.request_id) {
        retrySettingLinkToken(true);
      }
    },
  });

  useEffect(() => {
    if (error) {
      showErrorMessage('An error has occurred');
    }
  }, [error]);

  const loading = canceling || verifying || !ready;

  return { open, loading };
};

export default useVerifyPlaidLink;
